// Basketball daily card data — reads the B-16 snapshot (get_basketball_daily_card
// RPC): the day's 5-slot Game Card, Hardwood Chain, Ladder pool + counts. HONEST:
// null/empty until the spine runs (basketball is dormant) — the UI shows an
// honest-empty state, never a fabricated pick.

import { useMemo } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

export type CardSlot = "spread" | "total" | "q1" | "first_half" | "player_prop";

export interface CardPick {
  game_id: string;
  market_code: string;
  selection: string;
  value_pct: number;
  band: string;
  confidence?: string | null;
  needs_review?: boolean;
  flags?: string[];
}

export interface DailyCard {
  cardDate: string | null;
  computedAt: string | null;
  slots: Partial<Record<CardSlot, CardPick | null>>;
  filled: number;
  hardwoodRestDay: boolean;
  hardwoodPick: CardPick | null;
  ladderPool: CardPick[];
  considered: number;
  betCount: number;
}

export interface DailyCardState {
  loading: boolean;
  error: string | null;
  card: DailyCard | null; // null ⇒ no card yet (honest-empty)
  refetch: () => void;
}

interface RawCard {
  card_date: string | null;
  computed_at: string | null;
  game_card?: { slots?: Record<string, CardPick | null>; filled?: number } | null;
  hardwood?: { rest_day?: boolean; pick?: CardPick } | null;
  ladder_pool?: CardPick[] | null;
  counts?: { considered?: number; bet_count?: number; filled?: number } | null;
}

function shape(raw: RawCard | null): DailyCard | null {
  if (!raw) return null;
  return {
    cardDate: raw.card_date ?? null,
    computedAt: raw.computed_at ?? null,
    slots: (raw.game_card?.slots ?? {}) as Partial<Record<CardSlot, CardPick | null>>,
    filled: raw.game_card?.filled ?? raw.counts?.filled ?? 0,
    hardwoodRestDay: raw.hardwood?.rest_day !== false,
    hardwoodPick: raw.hardwood && raw.hardwood.rest_day === false ? (raw.hardwood.pick ?? null) : null,
    ladderPool: Array.isArray(raw.ladder_pool) ? raw.ladder_pool : [],
    considered: raw.counts?.considered ?? 0,
    betCount: raw.counts?.bet_count ?? 0,
  };
}

export function useBasketballDailyCard(): DailyCardState {
  const q = useRpcQuery<RawCard | null>("get_basketball_daily_card", { refetchInterval: 60_000 });
  const card = useMemo(() => shape(q.data ?? null), [q.data]);
  return useMemo(
    () => ({ loading: q.isLoading, error: q.error ? q.error.message : null, card, refetch: () => { void q.refetch(); } }),
    [q.isLoading, q.error, card, q.refetch],
  );
}
